/**
 * AI Chat Service
 *
 * API client for AI conversation and insight endpoints.
 */

import { ApiError, fetchApi, getApiBaseUrl, getCsrfToken } from '@/shared/services/api';
import type {
  AIConversation,
  AIConversationDetail,
  AIConversationList,
  AIInsightResponse,
  AIStreamEvent,
  AITransactionInsightResponse,
  PaginatedResponse,
} from '@/shared/types';

async function* streamMessage(
  conversationId: string,
  content: string,
  signal?: AbortSignal,
): AsyncGenerator<AIStreamEvent> {
  const csrfToken = getCsrfToken();
  const url = `${getApiBaseUrl()}/ai/conversations/${conversationId}/messages`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: JSON.stringify({ content }),
    signal,
  });

  if (!response.ok) {
    try {
      const error = await response.json();
      throw new ApiError(error.status || response.status, error.title || response.statusText, error.detail);
    } catch (e) {
      if (e instanceof ApiError) throw e;
      throw new ApiError(response.status, response.statusText);
    }
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const json = line.slice(6).trim();
        if (!json || json === '[DONE]') continue;

        const event: AIStreamEvent = JSON.parse(json);
        yield event;

        if (event.type === 'done') return;
        if (event.type === 'error') throw new Error(event.message);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export const aiService = {
  /** Create a new conversation */
  createConversation: () =>
    fetchApi<AIConversation>('/ai/conversations', { method: 'POST', body: {} }),

  /** Paginated list of conversations */
  listConversations: (params?: Record<string, unknown>) =>
    fetchApi<PaginatedResponse<AIConversationList>>('/ai/conversations', { params }),

  /** Get a single conversation with messages */
  getConversation: (id: string) =>
    fetchApi<AIConversationDetail>(`/ai/conversations/${id}`),

  /** Submit feedback on a message */
  submitFeedback: (messageId: string, feedback: -1 | 0 | 1) =>
    fetchApi<{ ok: boolean }>(`/ai/messages/${messageId}/feedback`, {
      method: 'POST',
      body: { feedback },
    }),

  /** Delete a conversation */
  deleteConversation: (id: string) =>
    fetchApi<void>(`/ai/conversations/${id}`, { method: 'DELETE' }),

  /** Get AI insight for a page */
  getPageInsight: (page: string) =>
    fetchApi<AIInsightResponse>(`/ai/insights/${page}`, { method: 'POST', body: {} }),

  /** Get AI insight for a specific transaction */
  getTransactionInsight: (txId: string) =>
    fetchApi<AITransactionInsightResponse>(`/ai/insights/transaction/${txId}`, { method: 'POST', body: {} }),

  /** Stream a message to a conversation (async generator) */
  streamMessage,
};
